import * as React from 'react';
import { Box, Button } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import Moveable from 'react-moveable';
import { t } from '@configuratorware/configurator-frontendgui/Framework/i18n';
import { ACCEPT, ALLOWED_TYPES, MAX_LAYER_SIZE, CROP_PADDING_PX, readFileAsDataUrl } from './fileUtils';

function loadImage(dataUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Image load failed'));
    img.src = dataUrl;
  });
}

/** Crop image to bounds (in same pixel space as image); add padding and clamp to image size. */
function cropToBounds(
  img: HTMLImageElement,
  bounds: { left: number; top: number; width: number; height: number }
): Promise<{ dataUrl: string; width: number; height: number }> {
  return new Promise((resolve) => {
    const pad = CROP_PADDING_PX;
    const sx = Math.max(0, Math.floor(bounds.left) - pad);
    const sy = Math.max(0, Math.floor(bounds.top) - pad);
    const cw = Math.min(img.naturalWidth - sx, Math.ceil(bounds.width) + 2 * pad);
    const ch = Math.min(img.naturalHeight - sy, Math.ceil(bounds.height) + 2 * pad);
    
    if (cw <= 0 || ch <= 0) {
      resolve({ dataUrl: img.src, width: img.naturalWidth, height: img.naturalHeight });
      return;
    }
    
    const canvas = document.createElement('canvas');
    canvas.width = cw;
    canvas.height = ch;
    const ctx = canvas.getContext('2d');
    
    if (!ctx) {
      resolve({ dataUrl: img.src, width: img.naturalWidth, height: img.naturalHeight });
      return;
    }
    
    ctx.drawImage(img, sx, sy, cw, ch, 0, 0, cw, ch);
    
    canvas.toBlob((blob) => {
      if (blob) {
        const objectUrl = URL.createObjectURL(blob);
        resolve({ dataUrl: objectUrl, width: cw, height: ch });
      } else {
        resolve({ dataUrl: img.src, width: img.naturalWidth, height: img.naturalHeight });
      }
    }, 'image/png');
  });
}

export interface SnapshotWithBoundsResult {
  dataUrl: string;
  contentBounds: { left: number; top: number; width: number; height: number } | null;
}

/** Normalize snapshot: string (legacy) or { dataUrl, contentBounds }. */
function normalizeSnapshot(
  value: string | SnapshotWithBoundsResult
): SnapshotWithBoundsResult {
  if (typeof value === 'string') {
    return { dataUrl: value, contentBounds: null };
  }
  return value;
}

export interface SavedFontLayerState {
  width: number;
  height: number;
  transform: string;
}

function captureLayerStateFromTarget(target: HTMLElement | null): SavedFontLayerState | null {
  if (!target) return null;
  const width = target.offsetWidth;
  const height = target.offsetHeight;
  if (width <= 0 || height <= 0) return null;
  return {
    width,
    height,
    transform: target.style.transform || '',
  };
}

function applyLayerStateToTarget(target: HTMLElement | null, state: SavedFontLayerState): void {
  if (!target) return;
  target.style.width = `${state.width}px`;
  target.style.height = `${state.height}px`;
  target.style.transform = state.transform;
}

interface PreviewOnStoneOverlayProps {
  open: boolean;
  onClose: () => void;
  backgroundImageUrl: string;
  getFontSnapshot: () => Promise<string | SnapshotWithBoundsResult>;
  onReplaceBackground: (dataUrl: string) => void;
}

export function PreviewOnStoneOverlay({
  open,
  onClose,
  backgroundImageUrl,
  getFontSnapshot,
  onReplaceBackground,
}: PreviewOnStoneOverlayProps) {
  const theme = useTheme();
  const w = (theme as { weha?: Record<string, unknown> }).weha || {};
  const containerRef = React.useRef<HTMLDivElement>(null);
  const targetRef = React.useRef<HTMLDivElement>(null);
  const moveableRef = React.useRef<Moveable>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const [fontLayerUrl, setFontLayerUrl] = React.useState<string | null>(null);
  const [layerWidth, setLayerWidth] = React.useState(0);
  const [layerHeight, setLayerHeight] = React.useState(0);
  const [originalSize, setOriginalSize] = React.useState<{ width: number; height: number } | null>(null);
  const [moveableReady, setMoveableReady] = React.useState(false);
  const [selected, setSelected] = React.useState(true);
  
  const objectUrlRef = React.useRef<string | null>(null);
  /** Per background image: last position, size, and rotation of the font layer. */
  const savedLayersByBackgroundRef = React.useRef<Record<string, SavedFontLayerState>>({});

  const persistLayerState = React.useCallback(() => {
    const state = captureLayerStateFromTarget(targetRef.current);
    if (state) {
      savedLayersByBackgroundRef.current[backgroundImageUrl] = state;
    }
  }, [backgroundImageUrl]);

  // Cleanup blob URLs when closed or unmounted
  React.useEffect(() => {
    if (!open && objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
  }, [open]);

  React.useEffect(() => {
    return () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
      }
    };
  }, []);

  React.useEffect(() => {
    if (!open || !backgroundImageUrl) return;
    let cancelled = false;
    getFontSnapshot()
      .then((raw) => {
        const snapshot = raw != null ? normalizeSnapshot(raw) : null;
        if (!snapshot?.dataUrl || cancelled) return null;
        return loadImage(snapshot.dataUrl).then((img) => {
          if (cancelled) return null;
          if (snapshot.contentBounds && snapshot.contentBounds.width > 0 && snapshot.contentBounds.height > 0) {
            return cropToBounds(img, snapshot.contentBounds);
          }
          return { dataUrl: img.src, width: img.naturalWidth, height: img.naturalHeight };
        });
      })
      .then((result) => {
        if (cancelled || !result) return;
        
        if (objectUrlRef.current) {
          URL.revokeObjectURL(objectUrlRef.current);
          objectUrlRef.current = null;
        }
        if (result.dataUrl.startsWith('blob:')) {
          objectUrlRef.current = result.dataUrl;
        }

        setFontLayerUrl(result.dataUrl);
        setOriginalSize({ width: result.width, height: result.height });

        const saved = savedLayersByBackgroundRef.current[backgroundImageUrl];
        if (saved) {
          setLayerWidth(saved.width);
          setLayerHeight(saved.height);
        } else {
          const maxW = typeof window !== 'undefined' ? window.innerWidth * MAX_LAYER_SIZE : 800;
          const maxH = typeof window !== 'undefined' ? window.innerHeight * MAX_LAYER_SIZE : 600;
          const scale = Math.min(maxW / result.width, maxH / result.height, 1);
          setLayerWidth(Math.round(result.width * scale));
          setLayerHeight(Math.round(result.height * scale));
        }
      })
      .catch((err) => {
        if (!cancelled) console.error('Preview snapshot failed', err);
      });
      
    return () => {
      cancelled = true;
      setFontLayerUrl(null);
      setLayerWidth(0);
      setLayerHeight(0);
      setOriginalSize(null);
      setMoveableReady(false);
    };
  }, [open, backgroundImageUrl, getFontSnapshot]);

  // Handle responsive resizing
  React.useEffect(() => {
    if (!originalSize || !open) return;

    const handleResize = () => {
      const maxW = typeof window !== 'undefined' ? window.innerWidth * MAX_LAYER_SIZE : 800;
      const maxH = typeof window !== 'undefined' ? window.innerHeight * MAX_LAYER_SIZE : 600;
      
      const scale = Math.min(maxW / originalSize.width, maxH / originalSize.height, 1);
      setLayerWidth(Math.round(originalSize.width * scale));
      setLayerHeight(Math.round(originalSize.height * scale));

      // Let the DOM update its sizing before asking Moveable to rebuild its target rectangle
      requestAnimationFrame(() => {
        moveableRef.current?.updateRect();
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [originalSize, open]);

  React.useEffect(() => {
    if (open) setSelected(true);
  }, [open]);

  React.useEffect(() => {
    if (!fontLayerUrl || layerWidth <= 0 || layerHeight <= 0) return;
    const id = requestAnimationFrame(() => {
      setMoveableReady(true);
    });
    return () => cancelAnimationFrame(id);
  }, [fontLayerUrl, layerWidth, layerHeight]);

  React.useEffect(() => {
    if (selected && moveableReady) {
      const timer = setTimeout(() => {
        moveableRef.current?.updateRect();
      }, 50); // 50ms is enough for the browser paint to catch up
      return () => clearTimeout(timer);
    }
  }, [selected, moveableReady, layerWidth, layerHeight]);

  /** Reapply saved position, scale, and rotation after the layer and Moveable are ready. */
  React.useEffect(() => {
    if (!open || !moveableReady || !fontLayerUrl) return;
    const saved = savedLayersByBackgroundRef.current[backgroundImageUrl];
    if (!saved) return;
    applyLayerStateToTarget(targetRef.current, saved);
    setLayerWidth(saved.width);
    setLayerHeight(saved.height);
    requestAnimationFrame(() => {
      moveableRef.current?.updateRect();
    });
  }, [open, moveableReady, fontLayerUrl, backgroundImageUrl]);

  const handleClose = () => {
    persistLayerState();
    onClose();
  };

  const handleReplaceClick = () => {
    fileInputRef.current?.click();
  };

  const handleReplaceFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !ALLOWED_TYPES.includes(file.type)) {
      e.target.value = '';
      return;
    }
    try {
      const dataUrl = await readFileAsDataUrl(file);
      onReplaceBackground(dataUrl);
    } catch (err) {
      console.error('Failed to read image', err);
    }
    e.target.value = '';
  };

  if (!open) return null;

  return (
    <Box
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1300,
        backgroundColor: '#111',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        ref={containerRef}
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'auto',
          cursor: selected ? 'default' : 'pointer',
        }}
        onClick={() => setSelected(false)}
      >
        <img
          src={backgroundImageUrl}
          alt=""
          style={{
            position: 'absolute',
            maxWidth: '100%',
            maxHeight: '100%',
            objectFit: 'contain',
            pointerEvents: 'none',
          }}
        />
        <Box
          style={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'auto',
          }}
          onClick={(e) => {
            e.stopPropagation();
            setSelected(true);
          }}
        >
          {fontLayerUrl && layerWidth > 0 && layerHeight > 0 && (
            <div
              ref={targetRef}
              style={{
                width: layerWidth,
                height: layerHeight,
                flexShrink: 0,
                pointerEvents: 'auto',
                cursor: 'move',
              }}
            >
              <img
                src={fontLayerUrl}
                alt=""
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  display: 'block',
                  pointerEvents: 'none',
                }}
              />
            </div>
          )}
        </Box>
      </Box>

      {selected && moveableReady && targetRef.current && containerRef.current && (
        <Moveable
          ref={moveableRef}
          target={targetRef.current}
          container={containerRef.current}
          draggable
          resizable
          rotatable={true}
          origin={false}
          bounds={{
            left: 0,
            top: 0,
            right: containerRef.current.offsetWidth,
            bottom: containerRef.current.offsetHeight,
          }}
          keepRatio={false}
          throttleDrag={0}
          onDrag={({ target, transform }) => {
            (target as HTMLElement).style.transform = transform;
          }}
          onDragEnd={persistLayerState}
          onRotate={({ target, transform }) => {
            (target as HTMLElement).style.transform = transform;
          }}
          onRotateEnd={persistLayerState}
          onResize={({ target, width, height, drag }) => {
            const el = target as HTMLElement;
            el.style.width = `${width}px`;
            el.style.height = `${height}px`;
            setLayerWidth(width);
            setLayerHeight(height);
            if (drag) {
              el.style.transform = drag.transform;
            }
          }}
          onResizeEnd={persistLayerState}
        />
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPT}
        onChange={handleReplaceFileChange}
        style={{ display: 'none' }}
      />

      <Box
        style={{
          position: 'absolute',
          bottom: 24,
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          gap: 12,
          zIndex: 1310,
          pointerEvents: 'auto',
        }}
      >
        <Button
          variant="contained"
          color="primary"
          onClick={handleReplaceClick}
          style={{
            borderRadius: (w.borderRadius as number) ?? theme.shape.borderRadius,
          }}
        >
          {t('weha.previewOnStone.replaceBackground')}
        </Button>
        <Button
          variant="contained"
          onClick={handleClose}
          style={{
            borderRadius: (w.borderRadius as number) ?? theme.shape.borderRadius,
          }}
        >
          {t('weha.previewOnStone.close')}
        </Button>
      </Box>
    </Box>
  );
}